import { useEffect, useCallback, useState } from "react";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import { updateUser } from "api/requests/updateUser";
// Chakra imports
import {
  Avatar,
  AvatarBadge,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Image,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Switch,
  Text,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import anya from "assets/img/avatars/anya.png";
import { HSeparator } from "components/Separator/Separator";
import { BsCircleFill, BsArrowRight } from "react-icons/bs";
import { RiComputerLine } from "react-icons/ri";
import { GiSmartphone } from "react-icons/gi";
import { BiEdit } from "react-icons/bi";
import { getUser } from "api/requests/getUser";
import { BeatLoader } from "react-spinners";
import { Card } from "@mantine/core";

function Settings() {
  const { colorMode } = useColorMode();
  const axiosPrivate = useAxiosPrivate();
  const [user, setUser] = useState();
  const [userData, setUserData] = useState(user);
  const [originalData] = useState({ ...user });
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedFile, setSelectedFile] = useState();

  // colors
  const bgColor = useColorModeValue("white", "#4A5058");
  const bgHead = useColorModeValue("hsla(0,0%,100%,.8)", "navy.800");
  const textColor = useColorModeValue("gray.600", "white");
  const bgCard = useColorModeValue(
    "linear-gradient(81.62deg, #343A40 2.25%, #212529 79.87%)",
    "navy.800"
  );

  // Basic info states
  const [firstname, setFirstname] = useState();
  const [lastname, setLastname] = useState();
  const [email, setEmail] = useState();
  const [phoneNumber, setPhoneNumber] = useState();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [show, setShow] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);

  const handleClick = () => setShow(!show);

  const fetchUser = async () => {
    try {
      const userInfo = await getUser(axiosPrivate);
      setUser(userInfo);
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    // Check if passwords match
    const passwordsDoMatch = newPassword === confirmNewPassword;
    setPasswordsMatch(passwordsDoMatch);

    // Validate new password requirements
    const isPasswordValid = validatePassword(newPassword);
    setPasswordValid(isPasswordValid);
  }, [newPassword, confirmNewPassword]);

  const validatePassword = (password) => {
    const requirementsRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,}$/;
    return requirementsRegex.test(password);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const saveChanges = async () => {
    const updatedFields = {};
    Object.keys(userData).forEach((key) => {
      if (userData[key] !== originalData[key]) {
        updatedFields[key] = userData[key];
      }
    });
    console.log(user._id);
    const response = await updateUser(axiosPrivate, updatedFields, user._id);
    console.log(response);
    if (response.status === 200) {
      toast({
        title: "Success!",
        description: "Profile update saved.",
        status: "success",
        duration: 2000,
      });
    } else if (response.status === 400) {
      toast({
        title: "Error!",
        description: `Current password is incorrect.`,
        status: "error",
        duration: 2000,
      });
    } else {
      toast({
        title: "Error!",
        description: `Error updating profile.`,
        status: "error",
        duration: 2000,
      });
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    // Sort files by name
    files.sort((a, b) => a.name.localeCompare(b.name));
    // Set only the first file if there is one
    setSelectedFile(files.length > 0 ? files[0] : null);
  };

  const handleDrop = useCallback((event) => {
    event.preventDefault();

    // Check if files were dropped directly or selected from file input
    const droppedFiles =
      event.dataTransfer.files.length > 0
        ? event.dataTransfer.files
        : event.target.files;

    const sortedFiles = Array.from(droppedFiles).sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    // Set only the first sorted file if there is one
    setSelectedFile(sortedFiles.length > 0 ? sortedFiles[0] : null);
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleSaveProfileImage = async (uploadedUrl) => {
    // Prepare the client data with the sorted arrays
    const userData = {
      image: uploadedUrl,
    };

    // Now, update the client in database with this new, organized data
    const response = await updateUser(axiosPrivate, userData, user?._id);
    // Handle the response (e.g., showing success or error messages)
    if (response.status === 200) {
      toast({
        title: "Success!",
        description: "Profile Image Saved.",
        status: "success",
        duration: 2000,
      });
      fetchUser();
    } else {
      toast({
        title: "Error!",
        description: `Status code: ${response.status}`,
        status: "error",
        duration: 2000,
      });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      // Assuming selectedFile is a single file, not an array
      console.error("No file selected");
      return;
    }

    // Configure AWS SDK credentials
    AWS.config.update({
      region: process.env.REACT_APP_AWS_REGION,
      accessKeyId: process.env.REACT_APP_S3_ACCESS_KEY_ID,
      secretAccessKey: process.env.REACT_APP_S3_SECRET_ACCESS_KEY,
    });
    const s3 = new AWS.S3();

    try {
      const userFolderKey = `${user?.imageDir}/${selectedFile.name}`; // Make sure user.imageDir exists and is a string
      const params = {
        Bucket: process.env.REACT_APP_S3_BUCKET_NAME,
        Key: userFolderKey,
        Body: selectedFile,
        ContentType: selectedFile.type,
      };

      const result = await s3.upload(params).promise();
      handleSaveProfileImage(result.Location); // Pass the URL of the uploaded file

      // Clear selected file after upload
      setSelectedFile(null);
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        title: "Error!",
        description: "Error uploading file.",
        status: "error",
        duration: 2000,
      });
    }
  };

  return (
    <>
      <Flex direction={"column"} pt={{ sm: "125px", lg: "75px" }} gap={4}>
        <Card
          id="profile"
          p={"sm"}
          w={"100%"}
          h={"fit-content"}
          bg={bgColor}
          withBorder
        >
          <Box>
            <Flex
              direction={{ sm: "column", md: "row" }}
              justify="space-between"
              align="center"
              w="100%"
            >
              <Flex align="center">
                <Avatar
                  border={"2px"}
                  src={user?.image}
                  w="80px"
                  h="80px"
                  me="22px"
                >
                  <AvatarBadge
                    borderRadius={"5px"}
                    border={"2px"}
                    boxSize={"1.25em"}
                    bg={"blue.500"}
                    cursor="pointer"
                    onClick={() => onOpen()}
                  >
                    <Icon as={BiEdit} boxSize={"0.75em"} />
                  </AvatarBadge>
                </Avatar>
                <Flex direction="column">
                  <Text color={textColor} fontWeight="bold" fontSize="lg">
                    {user?.firstname} {user?.lastname}
                  </Text>
                  <Text color="gray.400" fontWeight="normal" fontSize="sm">
                    {user?.email}
                  </Text>
                  <Stack direction={"row"} pt={1}>
                    <Badge colorScheme={"yellow"}>User</Badge>
                    {user?.roles?.Manager ? (
                      <Badge colorScheme={"orange"}>Manager</Badge>
                    ) : null}
                    {user?.roles?.Admin ? (
                      <Badge colorScheme={"red"}>Admin</Badge>
                    ) : null}
                    {user?.roles?.SuperAdmin ? (
                      <Badge colorScheme={"purple"}>SuperAdmin</Badge>
                    ) : null}
                  </Stack>
                </Flex>
              </Flex>
              <Flex w={"100%"} justify={"flex-end"} mb={"-35px"}>
                <Image w={"100px"} src={anya} />
              </Flex>
            </Flex>
          </Box>
        </Card>
        <Card
          id="info"
          bg={bgColor}
          withBorder
          p={"sm"}
          w={"100%"}
          h={"fit-content"}
        >
          <Box>
            <Text color={textColor} fontSize="lg" fontWeight="bold">
              Basic Info
            </Text>
          </Box>
          <Box>
            <Stack direction="column" spacing="20px" w="100%">
              <Stack direction="row" spacing={{ sm: "24px", lg: "30px" }}>
                <FormControl>
                  <FormLabel fontWeight="semibold" fontSize="xs" mb="10px">
                    First Name
                  </FormLabel>
                  <Input
                    variant="main"
                    name="firstname"
                    value={
                      firstname !== undefined
                        ? firstname
                        : user?.firstname || ""
                    }
                    fontSize="xs"
                    onChange={(e) => {
                      handleInputChange(e);
                      setFirstname(e.target.value);
                    }}
                    bg={useColorModeValue("white", "#343A40")}
                    color={textColor}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontWeight="semibold" fontSize="xs" mb="10px">
                    Last Name
                  </FormLabel>
                  <Input
                    variant="main"
                    name="lastname"
                    value={
                      lastname !== undefined ? lastname : user?.lastname || ""
                    }
                    fontSize="xs"
                    onChange={(e) => {
                      handleInputChange(e);
                      setLastname(e.target.value);
                    }}
                    bg={useColorModeValue("white", "#343A40")}
                    color={textColor}
                  />
                </FormControl>
              </Stack>
              <Stack
                direction={{ sm: "column", lg: "row" }}
                spacing={{ sm: "24px", lg: "30px" }}
              >
                <FormControl>
                  <FormLabel fontWeight="semibold" fontSize="xs" mb="10px">
                    Phone Number
                  </FormLabel>
                  <Input
                    variant="main"
                    name="phoneNumber"
                    value={
                      phoneNumber !== undefined
                        ? phoneNumber
                        : user?.phoneNumber || ""
                    }
                    fontSize="xs"
                    onChange={(e) => {
                      handleInputChange(e);
                      setPhoneNumber(e.target.value);
                    }}
                    bg={useColorModeValue("white", "#343A40")}
                    color={textColor}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontWeight="semibold" fontSize="xs" mb="10px">
                    Email Address
                  </FormLabel>
                  <Input
                    variant="main"
                    name="email"
                    value={email !== undefined ? email : user?.email || ""}
                    fontSize="xs"
                    onChange={(e) => {
                      handleInputChange(e);
                      setEmail(e.target.value);
                    }}
                    bg={useColorModeValue("white", "#343A40")}
                    color={textColor}
                  />
                </FormControl>
              </Stack>
              <Stack
                direction={{ sm: "column", lg: "row" }}
                spacing={{ sm: "24px", lg: "30px" }}
              >
                <FormControl>
                  <FormLabel fontWeight="semibold" fontSize="xs" mb="10px">
                    Username
                  </FormLabel>
                  <Input
                    disabled
                    variant="main"
                    name="username"
                    defaultValue={user?.username}
                    fontSize="xs"
                    onChange={() => {}}
                    readOnly
                    bg={useColorModeValue("white", "#343A40")}
                    color={textColor}
                  />
                </FormControl>
              </Stack>
            </Stack>
            <Flex w={"100%"} justify={"flex-end"} pt={"20px"}>
              <Button colorScheme={"red"} onClick={() => saveChanges()}>
                Save
              </Button>
            </Flex>
          </Box>
        </Card>
        <Flex w={"100%"} gap={4}>
          <Card
            id="change-password"
            bg={bgColor}
            withBorder
            p={"sm"}
            w={"100%"}
          >
            <Box>
              <Text color={textColor} fontSize="lg" fontWeight="semibold">
                Change Password
              </Text>
            </Box>
            <Box>
              <Stack direction="column" spacing="20px" w="100%">
                <FormControl>
                  <FormLabel fontWeight="semibold" fontSize="xs" mb="10px">
                    Current Password
                  </FormLabel>
                  <InputGroup size="md">
                    <Input
                      pr="4.5rem"
                      name="password"
                      type={show ? "text" : "password"}
                      placeholder="Current password"
                      value={currentPassword}
                      onChange={(e) => {
                        setCurrentPassword(e.target.value);
                        handlePasswordChange(e);
                      }}
                      bg={useColorModeValue("white", "#343A40")}
                      color={textColor}
                    />
                    <InputRightElement width="4.5rem">
                      <Button h="1.75rem" size="sm" onClick={handleClick}>
                        {show ? "Hide" : "Show"}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </FormControl>
                <FormControl isInvalid={!passwordsMatch}>
                  <FormLabel fontWeight="semibold" fontSize="xs" mb="10px">
                    New Password
                  </FormLabel>
                  <InputGroup size="md">
                    <Input
                      pr="4.5rem"
                      name="newPassword"
                      type={show ? "text" : "password"}
                      placeholder="New password"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        handlePasswordChange(e);
                      }}
                      bg={useColorModeValue("white", "#343A40")}
                      color={textColor}
                    />
                    <InputRightElement width="4.5rem">
                      <Button h="1.75rem" size="sm" onClick={handleClick}>
                        {show ? "Hide" : "Show"}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </FormControl>
                <FormControl isInvalid={!passwordsMatch}>
                  <FormLabel fontWeight="semibold" fontSize="xs" mb="10px">
                    Confirm New Password
                  </FormLabel>
                  <InputGroup size="md">
                    <Input
                      pr="4.5rem"
                      type={show ? "text" : "password"}
                      placeholder="Confirm New password"
                      value={confirmNewPassword}
                      onChange={(e) => {
                        setConfirmNewPassword(e.target.value);
                      }}
                      bg={useColorModeValue("white", "#343A40")}
                      color={textColor}
                    />
                    <InputRightElement width="4.5rem">
                      <Button h="1.75rem" size="sm" onClick={handleClick}>
                        {show ? "Hide" : "Show"}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  {!passwordValid && (
                    <FormErrorMessage>
                      Password does not meet requirements
                    </FormErrorMessage>
                  )}
                </FormControl>
                <Flex direction="column">
                  <Text
                    color={textColor}
                    fontWeight="bold"
                    fontSize="lg"
                    mb="4px"
                    mt="40px"
                  >
                    Password Requirements
                  </Text>
                  <Text color="gray.400" fontWeight="normal" fontSize="sm">
                    Please follow this guide for a strong password.
                  </Text>
                </Flex>
                <Flex
                  direction={{ sm: "column", lg: "row" }}
                  justify="space-between"
                  w="100%"
                >
                  <Stack
                    direction="column"
                    spacing="6px"
                    mb={{ sm: "12px", lg: "0px" }}
                  >
                    <Flex align="center">
                      <Icon
                        as={BsCircleFill}
                        w="6px"
                        h="6px"
                        color="gray.500"
                        me="6px"
                      />
                      <Text color="gray.500" fontWeight="normal" fontSize="xs">
                        One special characters
                      </Text>
                    </Flex>
                    <Flex align="center">
                      <Icon
                        as={BsCircleFill}
                        w="6px"
                        h="6px"
                        color="gray.500"
                        me="6px"
                      />
                      <Text color="gray.500" fontWeight="normal" fontSize="xs">
                        Min 6 characters
                      </Text>
                    </Flex>
                    <Flex align="center">
                      <Icon
                        as={BsCircleFill}
                        w="6px"
                        h="6px"
                        color="gray.500"
                        me="6px"
                      />
                      <Text color="gray.500" fontWeight="normal" fontSize="xs">
                        One number
                      </Text>
                    </Flex>
                  </Stack>
                  <Button
                    mt={4}
                    colorScheme="red"
                    type="submit"
                    isDisabled={!passwordsMatch || !passwordValid}
                    alignSelf={"flex-end"}
                    onClick={() => saveChanges()}
                  >
                    Change Password
                  </Button>
                </Flex>
              </Stack>
            </Box>
          </Card>
          <Flex direction={"column"} gap={4} w={"100%"}>
            <Card
              id="sessions"
              bg={bgColor}
              withBorder
              p={"sm"}
              w={"100%"}
              h={"100%"}
            >
              <Box>
                <Flex direction="column">
                  <Text
                    color={textColor}
                    fontSize="lg"
                    fontWeight="semibold"
                    mb="4px"
                  >
                    Sessions (WIP - Not Active)
                  </Text>
                  <Text color="gray.400" fontWeight="normal" fontSize="sm">
                    This is a list of devices that have logged into your
                    account. Remove those that you do not recognize.
                  </Text>
                </Flex>
              </Box>
              <Box>
                <Stack direction="column" spacing="18px" w="100%">
                  <Flex
                    direction={{ sm: "column", md: "row" }}
                    justify="space-between"
                    w="100%"
                  >
                    <Flex align="center">
                      <Icon
                        as={RiComputerLine}
                        me="30px"
                        w="28px"
                        h="28px"
                        color="gray.400"
                      />
                      <Flex direction="column" mb={{ sm: "8px", lg: null }}>
                        <Text
                          color={textColor}
                          fontWeight="normal"
                          fontSize="sm"
                        >
                          Westminster 68.133.163.201
                        </Text>
                        <Text
                          color="gray.400"
                          fontSize="xs"
                          fontWeight="normal"
                        >
                          Your current session
                        </Text>
                      </Flex>
                    </Flex>
                    <Stack
                      direction="row"
                      spacing="24px"
                      align="center"
                      alignSelf={{ sm: "flex-end", lg: null }}
                      minW={{ lg: "280px" }}
                    >
                      <Badge
                        bg={colorMode === "light" ? "green.100" : "green.400"}
                        color={colorMode === "light" ? "green.400" : "white"}
                        borderRadius="12px"
                        p="12px"
                      >
                        ACTIVE
                      </Badge>
                      <Text color="gray.400" fontSize="md" fontWeight="normal">
                        US
                      </Text>
                      <Button variant="no-effects" color="blue.500">
                        <Flex
                          align="center"
                          color="blue.500"
                          w="100%"
                          fontSize="sm"
                        >
                          <Text
                            me="6px"
                            transition="all .3s ease"
                            _hover={{ me: "14px" }}
                            fontSize="sm"
                          >
                            See more
                          </Text>
                          <Icon
                            as={BsArrowRight}
                            w="20px"
                            h="20px"
                            transition="all .3s ease"
                            _hover={{ transform: "translateX(50%)" }}
                          />
                        </Flex>
                      </Button>
                    </Stack>
                  </Flex>
                  <HSeparator />
                  <Flex
                    direction={{ sm: "column", md: "row" }}
                    justify="space-between"
                    w="100%"
                  >
                    <Flex align="center">
                      <Icon
                        as={RiComputerLine}
                        me="30px"
                        w="28px"
                        h="28px"
                        color="gray.400"
                      />
                      <Flex direction="column">
                        <Text
                          color={textColor}
                          fontWeight="normal"
                          fontSize="sm"
                        >
                          Chrome on macOS
                        </Text>
                      </Flex>
                    </Flex>
                    <Stack
                      direction="row"
                      spacing="24px"
                      align="center"
                      alignSelf={{ sm: "flex-end", lg: null }}
                      minW={{ lg: "185px" }}
                    >
                      <Text color="gray.400" fontSize="md" fontWeight="normal">
                        US
                      </Text>
                      <Button variant="no-effects" color="blue.500">
                        <Flex
                          align="center"
                          color="blue.500"
                          w="100%"
                          fontSize="sm"
                        >
                          <Text
                            me="6px"
                            transition="all .3s ease"
                            _hover={{ me: "14px" }}
                            fontSize="sm"
                          >
                            See more
                          </Text>
                          <Icon
                            as={BsArrowRight}
                            w="20px"
                            h="20px"
                            transition="all .3s ease"
                            _hover={{ transform: "translateX(50%)" }}
                          />
                        </Flex>
                      </Button>
                    </Stack>
                  </Flex>
                  <HSeparator />
                  <Flex
                    direction={{ sm: "column", md: "row" }}
                    justify="space-between"
                    w="100%"
                  >
                    <Flex align="center">
                      <Icon
                        as={GiSmartphone}
                        me="30px"
                        w="28px"
                        h="28px"
                        color="gray.400"
                      />
                      <Flex direction="column">
                        <Text
                          color={textColor}
                          fontWeight="normal"
                          fontSize="sm"
                        >
                          Safari on iPhone
                        </Text>
                      </Flex>
                    </Flex>
                    <Stack
                      direction="row"
                      spacing="24px"
                      align="center"
                      alignSelf={{ sm: "flex-end", lg: null }}
                      justifySelf="flex-end"
                      minW={{ lg: "185px" }}
                    >
                      <Text color="gray.400" fontSize="md" fontWeight="normal">
                        US
                      </Text>
                      <Button variant="no-effects" color="blue.500">
                        <Flex
                          align="center"
                          color="blue.500"
                          w="100%"
                          fontSize="sm"
                        >
                          <Text
                            me="6px"
                            transition="all .3s ease"
                            _hover={{ me: "14px" }}
                            fontSize="sm"
                          >
                            See more
                          </Text>
                          <Icon
                            as={BsArrowRight}
                            w="20px"
                            h="20px"
                            transition="all .3s ease"
                            _hover={{ transform: "translateX(50%)" }}
                          />
                        </Flex>
                      </Button>
                    </Stack>
                  </Flex>
                </Stack>
              </Box>
            </Card>
          </Flex>
        </Flex>
      </Flex>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Profile Image Upload</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box pb={5}>
              <InputGroup>
                <InputLeftAddon children="S3 Directory" />
                <Input
                  type="text"
                  color={"black"}
                  disabled
                  placeholder={user?.imageDir}
                />
              </InputGroup>
            </Box>
            <Box style={{ width: "100%" }}>
              <Box
                bg={colorMode === "dark" ? "navy.700" : "gray.300"}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                style={{
                  width: "100%",
                  height: "200px",
                  border: "1px solid",
                  borderColor: "#d0dcfb",
                  borderRadius: "8px",
                  textAlign: "center",
                  lineHeight: "200px",
                  marginBottom: "20px",
                }}
              >
                <Box>
                  <Button
                    onClick={() =>
                      document.querySelector('input[type="file"]').click()
                    }
                  >
                    Drag & Drop a file here or click to select
                  </Button>
                </Box>
              </Box>
              <input
                type="file"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Text mr={"107px"}>Files Selected: {selectedFile ? 1 : 0} </Text>
            <Button
              colorScheme="blue"
              onClick={() => handleUpload(selectedFile)}
              isLoading={selectedFile ? false : true}
              spinner={<BeatLoader size={8} color="white" />}
              mr={3}
            >
              Upload
            </Button>
            <Button colorScheme={"blue"} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default Settings;
